import React, { useState, useEffect } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import {
  restoreElements,
  restoreAppState,
  CaptureUpdateAction,
} from "@excalidraw/excalidraw";
import { useAtom } from "../app-jotai";
import {
  activeCanvasAtom,
  authSessionAtom,
  canvasListDialogStateAtom,
  saveCanvasDialogStateAtom,
} from "./auth-state";
import { canvasApi } from "./auth-api";
import type { Canvas } from "@restack/shared";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import "./CanvasListDialog.scss";

export const CanvasListDialog: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = ({ excalidrawAPI }) => {
  const [dialogState, setDialogState] = useAtom(canvasListDialogStateAtom);
  const [, setSaveCanvasDialog] = useAtom(saveCanvasDialogStateAtom);
  const [session] = useAtom(authSessionAtom);
  const [, setActiveCanvas] = useAtom(activeCanvasAtom);

  const [canvases, setCanvases] = useState<Canvas[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openingCanvasId, setOpeningCanvasId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchCanvases = async () => {
    if (!session.accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await canvasApi.list(session.accessToken);
      setCanvases(res.canvases || []);
    } catch (err: any) {
      setError(err.message || "Failed to load cloud canvases");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dialogState.isOpen && session.accessToken) {
      fetchCanvases();
    }
  }, [dialogState.isOpen, session.accessToken]);

  if (!dialogState.isOpen) {
    return null;
  }

  const handleClose = () => {
    setDialogState({ isOpen: false });
  };

  const handleNewBlankCanvas = () => {
    setActiveCanvas(null);
    excalidrawAPI?.resetScene({ resetLoadingState: true });
    excalidrawAPI?.setToast({
      message: "Started new blank canvas",
      duration: 2500,
      closable: true,
    });
    handleClose();
  };

  const handleLoadCanvas = async (canvas: Canvas) => {
    if (!excalidrawAPI) return;
    setOpeningCanvasId(canvas.id);
    try {
      const res = await canvasApi.get(canvas.id, session.accessToken);
      const fullCanvas = res.canvas;
      const sceneData = fullCanvas?.data as any;

      if (sceneData) {
        // Reset scene first so old elements/state are cleared
        excalidrawAPI.resetScene({ resetLoadingState: true });

        const rawElements = Array.isArray(sceneData.elements)
          ? sceneData.elements
          : [];
        const rawAppState = sceneData.appState || {};

        const restoredElements = restoreElements(rawElements, null, {
          repairBindings: true,
        });
        const restoredAppState = restoreAppState(rawAppState, null);

        if (sceneData.files && typeof sceneData.files === "object") {
          excalidrawAPI.addFiles(Object.values(sceneData.files));
        }

        excalidrawAPI.updateScene({
          elements: restoredElements,
          appState: {
            ...restoredAppState,
            isLoading: false,
          },
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });

        setActiveCanvas({ id: fullCanvas.id, name: fullCanvas.name });

        excalidrawAPI.setToast({
          message: `Loaded "${fullCanvas.name}" from Cloud`,
          duration: 3000,
          closable: true,
        });
        handleClose();
      } else {
        throw new Error("Canvas data is empty or corrupted");
      }
    } catch (err: any) {
      alert("Failed to load canvas: " + (err.message || "Unknown error"));
    } finally {
      setOpeningCanvasId(null);
    }
  };

  const handleDeleteCanvas = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await canvasApi.delete(id, session.accessToken);
      setCanvases((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      alert("Failed to delete canvas: " + err.message);
    }
  };

  return (
    <Dialog
      size="regular"
      onCloseRequest={handleClose}
      title={
        <div className="CanvasListDialog__header-title">
          <span>My Cloud Canvases</span>
          <button
            type="button"
            className="CanvasListDialog__close-btn"
            onClick={handleClose}
            title="Close (Esc)"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="CanvasListDialog">
        <div className="CanvasListDialog__toolbar">
          <span className="CanvasListDialog__count">
            {canvases.length} saved {canvases.length === 1 ? "canvas" : "canvases"}
          </span>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <button
              type="button"
              className="CanvasListDialog__secondary-btn"
              onClick={handleNewBlankCanvas}
              title="Start a new blank whiteboard"
            >
              <span>+</span>
              <span>New Blank</span>
            </button>
            <button
              type="button"
              className="CanvasListDialog__new-btn"
              onClick={() => {
                handleClose();
                setSaveCanvasDialog({ isOpen: true });
              }}
            >
              <span>+</span>
              <span>Save Current Canvas</span>
            </button>
          </div>
        </div>

        {error && (
          <div
            style={{
              padding: "0.6rem 0.8rem",
              background: "rgba(250, 82, 82, 0.1)",
              borderRadius: "8px",
              color: "#e03131",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="CanvasListDialog__loading">
            Loading your canvases...
          </div>
        ) : canvases.length === 0 ? (
          <div className="CanvasListDialog__empty">
            <h3>No cloud canvases yet</h3>
            <p>
              Save your current drawing to the cloud to access it from any device
              or share it with your team.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", marginTop: "0.5rem" }}>
              <button
                type="button"
                className="CanvasListDialog__back-btn"
                onClick={handleClose}
              >
                ← Back to Canvas
              </button>
              <button
                type="button"
                className="CanvasListDialog__new-btn"
                onClick={() => {
                  handleClose();
                  setSaveCanvasDialog({ isOpen: true });
                }}
              >
                + Save Current Canvas
              </button>
            </div>
          </div>
        ) : (
          <div className="CanvasListDialog__list">
            {canvases.map((canvas) => (
              <div key={canvas.id} className="CanvasListDialog__item">
                <div className="CanvasListDialog__info">
                  <span className="CanvasListDialog__title">{canvas.name}</span>
                  <span className="CanvasListDialog__meta">
                    {canvas.description ? `${canvas.description} • ` : ""}
                    Updated{" "}
                    {canvas.updatedAt
                      ? new Date(canvas.updatedAt).toLocaleDateString()
                      : "Recently"}
                  </span>
                </div>
                <div className="CanvasListDialog__actions">
                  <button
                    type="button"
                    className="CanvasListDialog__open-btn"
                    disabled={openingCanvasId === canvas.id}
                    onClick={() => handleLoadCanvas(canvas)}
                  >
                    {openingCanvasId === canvas.id ? "Opening..." : "Open Canvas"}
                  </button>
                  <button
                    type="button"
                    className="CanvasListDialog__delete-btn"
                    onClick={() => handleDeleteCanvas(canvas.id, canvas.name)}
                    title="Delete canvas"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="CanvasListDialog__footer">
          <button
            type="button"
            className="CanvasListDialog__back-btn"
            onClick={handleClose}
          >
            ← Back to Whiteboard
          </button>
        </div>
      </div>
    </Dialog>

  );
};
